/* Self Rez coffee finder. All matching runs locally on Shopify-rendered data. */
(() => {
  if (customElements.get('srz-coffee-finder')) return;

  const choices = {
    type: [['any', 'Any type'], ['classic', 'Classic'], ['flavored', 'Flavored'], ['functional', 'Functional']],
    taste: [['any', 'Any taste'], ['chocolate', 'Chocolate & cocoa'], ['fruity', 'Fruit & floral'], ['earthy', 'Earthy & smoky'], ['flavored', 'Flavored & sweet']],
    roast: [['any', 'Any roast'], ['light', 'Light'], ['medium', 'Medium'], ['medium_dark', 'Medium dark'], ['dark', 'Dark']],
    caffeine: [['any', 'Any caffeine'], ['regular', 'Regular'], ['high', 'High caffeine'], ['decaf', 'Decaf']]
  };
  const keyLabels = { type: 'coffee type', taste: 'taste', roast: 'roast', caffeine: 'caffeine' };
  const label = (key, value) => choices[key].find(item => item[0] === value)?.[1] || '';

  class SelfRezCoffeeFinder extends HTMLElement {
    connectedCallback() {
      const start = () => {
        if (!this.querySelector('[data-coffee-data]')) return false;
        this.observer?.disconnect();
        this.initialize();
        return true;
      };
      if (!start()) {
        this.observer = new MutationObserver(start);
        this.observer.observe(this, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.initialized = false;
    }

    initialize() {
      if (this.initialized) return;
      const root = this;
      const $ = selector => root.querySelector(selector);
      let records;
      try { records = JSON.parse($('[data-coffee-data]').textContent); }
      catch (error) { console.error('Self Rez coffee finder: invalid product data.', error); return; }
      if (!Array.isArray(records)) return;
      this.initialized = true;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const on = (element, event, callback) => element.addEventListener(event, callback, { signal });
      const hideUnavailable = root.dataset.hideUnavailable !== 'false';
      const limit = Math.max(3, Math.min(12, Number(root.dataset.limit) || 6));
      const smallScreen = matchMedia('(max-width: 620px)');
      const visibleLimit = () => smallScreen.matches ? Math.min(3, limit) : limit;
      const state = { type: 'any', taste: 'any', roast: 'any', caffeine: 'any' };
      const seen = new Set();
      const coffees = records.filter(p => {
        if (!p.id || seen.has(p.id) || !Array.isArray(p.offers)) return false;
        seen.add(p.id);
        return !hideUnavailable || p.offers.some(offer => offer.available);
      });
      let selected = coffees[0]?.id, openKey = null, lastChanged = null, remedy = [], expanded = false;
      let matches = [], drawn = false;
      const node = key => $(`[data-node="${key}"]`);
      const changed = () => Object.values(state).some(value => value !== 'any');
      const eligibleOffers = p => p.offers.filter(offer => !hideUnavailable || offer.available);
      const fit = (p, picks) => Object.entries(picks).every(([key, value]) => value === 'any' ||
        (key === 'taste' ? p.taste.includes(value) : key === 'roast' ? eligibleOffers(p).some(offer => offer.roast === value) : p[key] === value));
      const emit = (action, product) => root.dispatchEvent(new CustomEvent('srz:coffee-finder', {
        bubbles: true, detail: { action, filters: { ...state }, product: product?.handle || null, matches: matches.length }
      }));

      function pickChoice(choice) {
        state[choice.dataset.key] = choice.dataset.value; lastChanged = choice.dataset.key;
        expanded = false; closePanel(lastPointer === 'keyboard'); draw(); emit('filter');
      }

      function closePanel(restore = true) {
        if (!openKey) return;
        const current = node(openKey), trigger = current.querySelector('.or-trigger');
        current.querySelector('.or-panel').hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.querySelector('.or-label > span').textContent = '+';
        openKey = null;
        if (restore) trigger.focus({ preventScroll: true });
      }

      let lastPointer = 'mouse', pointerPick = null;
      function keepInView(panel) {
        // Bottom nodes open upward on phones; keep the whole panel clear of the sticky header.
        const header = document.querySelector('sticky-header, .header-wrapper, #MainHeader');
        const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const box = panel.getBoundingClientRect();
        let dy = 0;
        if (box.top < top + 8) dy = box.top - top - 8;
        else if (box.bottom > innerHeight - 8) dy = Math.min(box.bottom - innerHeight + 8, box.top - top - 8);
        if (dy) window.scrollBy({ top: dy, behavior: 'instant' });
      }

      function openPanel(key) {
        if (openKey === key) { closePanel(); return; }
        closePanel(false);
        openKey = key;
        const current = node(key), trigger = current.querySelector('.or-trigger');
        current.querySelector('.or-panel').hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        trigger.querySelector('.or-label > span').textContent = '−';
        // Moving focus on a touch/mouse open is what let Safari's tap-without-focus close the
        // panel before the choice registered; only move focus for keyboard users.
        if (lastPointer === 'keyboard') current.querySelector('.or-choice[aria-pressed="true"]').focus({ preventScroll: true });
        keepInView(current.querySelector('.or-panel'));
      }

      function bestOffer(p) {
        return p.offers.filter(offer => offer.available && (state.roast === 'any' || offer.roast === state.roast))
          .sort((a, b) => a.price - b.price)[0];
      }

      function choose(p, animate) {
        const previous = selected;
        selected = p.id;
        const photo = $('.or-photo');
        photo.hidden = !p.image;
        $('.or-photo-fallback').hidden = Boolean(p.image);
        if (p.image) photo.src = p.image;
        else photo.removeAttribute('src');
        photo.alt = p.name + ' coffee';
        $('.or-name').textContent = p.name;
        $('.or-notes').textContent = p.notes || '';
        const roasts = state.roast === 'any' ? eligibleOffers(p).map(offer => label('roast', offer.roast)).filter(Boolean) : [label('roast', state.roast)];
        const facts = [];
        if (roasts.length) facts.push(roasts.join(' / ') + ' roast');
        if (p.caffeine !== 'unspecified') facts.push(p.caffeine === 'regular' ? 'Regular caffeine' : label('caffeine', p.caffeine));
        $('.or-facts').textContent = facts.join(' · ').toUpperCase();
        const offer = bestOffer(p);
        const url = new URL(p.url, window.location.href);
        // Preserve a chosen roast without preselecting an arbitrary grind when no roast was chosen.
        if (offer && state.roast !== 'any' && p.offers.length > 1) url.searchParams.set('variant', offer.variantId);
        $('.or-buy').href = url.href;
        $('.or-price').textContent = offer ? 'From ' + offer.money : 'Sold out';
        const action = p.member ? 'VIEW MEMBER COFFEE' : offer ? 'SHOP THIS COFFEE' : 'VIEW COFFEE';
        $('.or-buy-label').textContent = action;
        $('.or-buy').setAttribute('aria-label', action.toLowerCase() + ' ' + p.name + (offer ? ', from ' + offer.money : ', currently sold out'));
        $('.or-special').hidden = !p.special && !p.member;
        $('.or-special').textContent = p.special || (p.member ? 'Members only' : '');
        $('.or-next').textContent = !offer ? 'Currently unavailable. View the product for details.' : p.member ? 'Members only. View access details on the product page.' : 'Choose your size and grind next.';
        root.querySelectorAll('.or-pick').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.productId === selected)));
        if (animate && previous !== selected && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          photo.animate([{ transform: 'translateY(7px) scale(.97)' }, { transform: 'translateY(0) scale(1)' }], { duration: 230, easing: 'ease-out' });
        }
      }

      function updateChoices() {
        Object.entries(choices).forEach(([key, values]) => {
          node(key).querySelector('.or-value').textContent = label(key, state[key]);
          node(key).querySelector('.or-trigger').classList.toggle('is-picked', state[key] !== 'any');
          const list = node(key).querySelector('.or-options');
          list.replaceChildren();
          values.forEach(([value, title]) => {
            const count = coffees.filter(p => fit(p, { ...state, [key]: value })).length;
            const button = document.createElement('button');
            button.type = 'button'; button.className = 'or-choice';
            button.dataset.key = key; button.dataset.value = value;
            button.setAttribute('aria-pressed', String(state[key] === value));
            const name = document.createElement('span'), total = document.createElement('small');
            name.textContent = title; total.textContent = count + (count === 1 ? ' match' : ' matches');
            button.append(name, total); list.append(button);
          });
        });
      }

      function drawResults() {
        const result = $('.or-results');
        result.replaceChildren();
        const visible = expanded ? matches : matches.slice(0, visibleLimit());
        // Keep the featured choice represented if it survived a filter change.
        const chosen = matches.find(p => p.id === selected);
        if (chosen && !visible.includes(chosen) && visible.length) visible[visible.length - 1] = chosen;
        visible.forEach(p => {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'or-pick';
          button.dataset.productId = p.id; button.dataset.handle = p.handle;
          button.setAttribute('aria-pressed', String(p.id === selected));
          button.setAttribute('aria-label', 'Preview ' + p.name + (p.member ? ', members only' : ''));
          if (root.dataset.designMode === 'true') button.setAttribute('data-shopify-editor-block', JSON.stringify({ id: p.blockId, type: 'coffee' }));
          if (p.thumbnail) {
            const image = document.createElement('img');
            image.src = p.thumbnail; image.alt = ''; image.width = 80; image.height = 80;
            image.loading = 'lazy'; image.decoding = 'async'; button.append(image);
          }
          const content = document.createElement('span'); content.className = 'or-pick-body';
          const title = document.createElement('span'); title.className = 'or-pick-name'; title.textContent = p.name;
          const facts = document.createElement('span'); facts.className = 'or-pick-facts';
          const roasts = state.roast === 'any' ? eligibleOffers(p).map(offer => label('roast', offer.roast)).filter(Boolean) : [label('roast', state.roast)];
          const caffeine = p.caffeine === 'regular' ? 'Regular caffeine' : label('caffeine', p.caffeine);
          facts.textContent = [roasts.length ? roasts.join(' / ') + ' roast' : '', caffeine].filter(Boolean).join(' · ');
          const notes = document.createElement('span'); notes.className = 'or-pick-notes'; notes.textContent = p.notes || '';
          content.append(title, facts, notes);
          if (p.member || !bestOffer(p)) {
            const badge = document.createElement('small');
            badge.textContent = p.member ? 'Members only' : 'Sold out'; content.append(badge);
          }
          const mark = document.createElement('span'); mark.className = 'or-pick-mark'; mark.setAttribute('aria-hidden', 'true');
          button.append(content, mark); result.append(button);
        });
        $('.or-more').hidden = matches.length <= visibleLimit();
        $('.or-more').setAttribute('aria-expanded', String(expanded));
        $('.or-more').textContent = expanded ? 'Show fewer matches' : 'View all ' + matches.length + ' matches';
      }

      function makeRemedy() {
        const keys = Object.keys(state).filter(key => state[key] !== 'any');
        const alternatives = keys.filter(key => key !== lastChanged), possibilities = [];
        for (let mask = 1; mask < (1 << alternatives.length); mask++) possibilities.push(alternatives.filter((key, index) => mask & (1 << index)));
        possibilities.sort((a, b) => a.length - b.length);
        remedy = possibilities.find(clear => coffees.some(p => fit(p, Object.fromEntries(Object.entries(state).map(([key, value]) => [key, clear.includes(key) ? 'any' : value]))))) || keys;
        const keeps = keys.filter(key => !remedy.includes(key));
        const names = keeps.map(key => label(key, state[key]).toLowerCase() + (key === 'roast' ? ' roast' : ''));
        $('.or-recovery-copy').textContent = 'Nothing fits all of these picks. Try this adjustment:';
        $('.or-relax').textContent = (names.length ? 'Keep ' + names.join(' + ') + ' · ' : '') + 'Clear ' + remedy.map(key => keyLabels[key]).join(' and ');
      }

      function draw() {
        matches = coffees.filter(p => fit(p, state));
        const has = matches.length > 0;
        $('.or-product').hidden = !has; $('.or-empty').hidden = has;
        $('.or-shop').hidden = !has; $('.or-recovery').hidden = has;
        $('.or-kicker').textContent = has ? (changed() ? 'MATCHES YOUR PICKS' : 'A GOOD PLACE TO START') : 'YOUR PICKS ARE STILL SAVED';
        $('.or-count').textContent = has ? matches.length + (matches.length === 1 ? ' coffee' : ' coffees') + (changed() ? (matches.length === 1 ? ' matches' : ' match') + ' your picks' : ' to explore') : 'No exact matches';
        $('.or-reset').hidden = !changed();
        $('.or-results-hint').hidden = !has;
        updateChoices();
        if (has) choose(matches.find(p => p.id === selected) || matches[0], drawn);
        else makeRemedy();
        drawResults();
        drawn = true;
      }

      on(smallScreen, 'change', () => { if (drawn) drawResults(); });

      on(root, 'click', event => {
        const trigger = event.target.closest('.or-trigger');
        if (trigger && root.contains(trigger)) { openPanel(trigger.closest('.or-node').dataset.node); return; }
        if (event.target.closest('.or-close')) { closePanel(); return; }
        const choice = event.target.closest('.or-choice');
        if (choice) {
          clearTimeout(fallback); pointerPick = null;
          pickChoice(choice); return;
        }
        const pick = event.target.closest('.or-pick');
        if (pick) {
          const p = matches.find(coffee => coffee.id === pick.dataset.productId);
          if (p) {
            closePanel(false); choose(p, true);
            $('.or-count').textContent = 'Viewing ' + p.name + ' · ' + matches.length + (matches.length === 1 ? ' match' : ' matches');
            // Bring the selected coffee into view when it is away from the results.
            const shopBox = $('.or-shop').getBoundingClientRect();
            if (shopBox.bottom < 150 || shopBox.top > innerHeight || matchMedia('(max-width: 979px)').matches) {
              $('.or-name').focus({ preventScroll: true });
              $('.or-center').scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
            }
            emit('preview', p);
          }
          return;
        }
        if (event.target.closest('.or-reset')) {
          closePanel(false); Object.keys(state).forEach(key => state[key] = 'any');
          lastChanged = null; expanded = false; draw(); node('type').querySelector('.or-trigger').focus({ preventScroll: true }); emit('reset'); return;
        }
        if (event.target.closest('.or-relax')) {
          remedy.forEach(key => state[key] = 'any'); expanded = false; draw();
          $('.or-buy').focus({ preventScroll: true }); emit('adjust'); return;
        }
        if (event.target.closest('.or-more')) { expanded = !expanded; drawResults(); emit('expand'); return; }
        if (event.target.closest('.or-buy')) emit('shop', matches.find(p => p.id === selected));
      });
      on(root, 'keydown', event => { lastPointer = 'keyboard'; });
      let downAt = null, fallback = 0;
      on(root, 'pointerdown', event => {
        lastPointer = event.pointerType || 'mouse';
        downAt = { x: event.clientX, y: event.clientY };
      }, true);
      // Fallback for the click: iOS Safari can withhold the click after a tap (hover/content-change
      // heuristics, third-party overlays settling). If a tap ends on a choice and no click follows
      // within 350ms, apply it anyway. The click path stays primary, so nothing is applied twice and
      // no re-render happens under the finger before the click (which would cause a ghost click).
      on(root, 'pointerup', event => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const choice = event.target.closest('.or-choice');
        if (!choice || !openKey || !node(openKey).contains(choice) || !downAt) return;
        if (Math.abs(event.clientX - downAt.x) > 10 || Math.abs(event.clientY - downAt.y) > 10) return;
        clearTimeout(fallback);
        pointerPick = choice;
        fallback = setTimeout(() => {
          if (pointerPick === choice && choice.isConnected) { pointerPick = null; pickChoice(choice); }
        }, 350);
      });
      on(root, 'pointercancel', () => { downAt = null; });
      on(root, 'keydown', event => {
        if (event.key === 'Escape' && openKey) { event.preventDefault(); closePanel(); }
      });
      // iOS/macOS Safari do not focus buttons on tap, so focusout fires with relatedTarget null
      // before the click lands and the panel closed under the finger. Only close when focus
      // really moves to another element; outside taps are handled by the listeners below.
      on(root, 'focusout', event => {
        if (openKey && event.relatedTarget && !node(openKey).contains(event.relatedTarget)) closePanel(false);
      });
      // Close on a completed tap/click outside, not on pointerdown: on phones a scroll gesture
      // starts with pointerdown, which used to close the panel while the user scrolled to an option.
      // iOS does not send click for taps on non-interactive areas, so a still pointerup also counts.
      let outsideDown = null;
      const outside = event => openKey && !node(openKey).contains(event.target);
      on(document, 'pointerdown', event => { outsideDown = { x: event.clientX, y: event.clientY }; });
      on(document, 'pointerup', event => {
        if (outsideDown && outside(event) && Math.abs(event.clientX - outsideDown.x) <= 10 && Math.abs(event.clientY - outsideDown.y) <= 10) closePanel(false);
        outsideDown = null;
      });
      on(document, 'pointercancel', () => { outsideDown = null; });
      on(document, 'click', event => { if (outside(event)) closePanel(false); });
      on($('.or-photo'), 'error', () => { $('.or-photo').hidden = true; $('.or-photo-fallback').hidden = false; });
      on(root, 'shopify:block:select', event => {
        const coffee = coffees.find(p => p.blockId === event.detail.blockId);
        if (!coffee) return;
        Object.keys(state).forEach(key => state[key] = 'any');
        selected = coffee.id; expanded = true; closePanel(false); draw();
      });

      if (coffees.length) {
        draw(); $('.or-fallback').hidden = true; $('.or-interactive').hidden = false;
      } else {
        $('.or-fallback > p').textContent = root.dataset.designMode === 'true' ? 'Choose available products in the coffee blocks to populate this finder.' : 'Explore the current lineup in our coffee collection.';
      }
    }
  }
  customElements.define('srz-coffee-finder', SelfRezCoffeeFinder);
})();
